/* eslint-disable jsx-a11y/accessible-emoji */
import React from 'react'
import _ from 'lodash'
import Moment from 'moment'

import * as API from './api'
import { copyTextToClipboard, ERROR_LEVEL, FATAL_LEVEL, getUsername, LOG_LEVEL } from "./utils";

import "./service.css"
import Alerts from './alerts';
import Heartbeat from './widgets/heartbeat';

export default class Service extends React.Component {
	constructor(props) {
		super(props);
		this.serviceId = props.params && props.params.serviceId;
		this.state = {
			service: null,
            originalService: null,
            notifications: null,
            logs: null,
            errors: null,
            displayType: "errors",
		};
        this.serviceFetchInterval = null;
	}

	componentDidMount() {
        this.fetchData();
	}

    componentDidUpdate() {
        const serviceId = this.props.params && this.props.params.serviceId;
        if (serviceId !== this.serviceId) {
            this.serviceId = serviceId;
            this.fetchData();
        }
    }

    fetchData = () => {
        this.setState({
            service: null,
            originalService: null,
            notifications: null,
            logs: null,
            errors: null,
            displayType: "errors",
        });
        API.fetchService(this.serviceId).then(response => {
            this.setState({
                service: response,
                originalService: response,
            });
        });
        this.fetchServiceNotifications(this.serviceId);
        this.fetchLogs(this.serviceId);

        clearInterval(this.serviceFetchInterval);
        this.serviceFetchInterval = setInterval(() => {
            API.fetchService(this.serviceId).then(response => {
                // Don't update all the fields so that the user can continue editing
                const updatedService = {
                    ...this.state.service,
                    eventInfo: response.eventInfo,
                    lastRun: response.lastRun,
                    lastRunResult: response.lastRunResult,
                    lastRunMessage: response.lastRunMessage,
                    nextPlannedRun: response.nextPlannedRun,
                };
                this.setState({
                    service: updatedService,
                    originalService: updatedService,
                });
            });
            this.fetchServiceNotifications(this.serviceId);
            this.fetchLogs(this.serviceId);
        }, 30000);
    }

    fetchServiceNotifications = (serviceId) => {
        API.fetchServiceNotifications(serviceId).then(response => {
            this.setState({
                notifications: {
                    items: _.orderBy(response, ["created"], ["desc"]),
                    numberOfItems: response.length,
                },
            });
        });
    }

    fetchLogs = (serviceId) => {
        const logRequests = [
            API.fetchLogs(LOG_LEVEL, null, serviceId),
            API.fetchLogs(ERROR_LEVEL, null, serviceId),
            API.fetchLogs(FATAL_LEVEL, null, serviceId),
        ];
        Promise.all(logRequests).then(([logs, errors, fatals]) => {
            const errorItems = [...fatals.items, ...errors.items];
            const numberOfErrorItems = fatals.numberOfItems + errors.numberOfItems;
            this.setState({
                logs: {
                    items: _.orderBy(logs.items, ["lastRun"], ["desc"]),
                    numberOfItems: logs.numberOfItems,
                },
                errors: {
                    items: _.orderBy(errorItems, ["lastRun"], ["desc"]),
                    numberOfItems: numberOfErrorItems,
                },
            });
        });
    }

    onChange = (key, value) => {
        this.setState({
            service: {
                ...this.state.service,
                [key]: value,
            },
        });
    }

    save = () => {
        API.updateService(this.state.service).then(response => {
            this.setState({
                service: response,
                originalService: response,
            });
        });
    }

    cancel = () => {
        this.setState({
            service: this.state.originalService,
        });
    }

    close = () => {
        this.props.router.replace("/");
    }

    handleNotificationAction = (notificationId, actionId) => {
        const name = getUsername();
        API.postNotificationAction(this.state.service.id, notificationId, actionId, name).then(() => {
            this.fetchServiceNotifications(this.state.service.id);
        });
    }

	render() {
        if (!this.state.service) {
            return null;
        }

        const {
            name,
            description,
            windowsServiceDisplayName,
            alertOnStop,
            // maxRunTime,
            priority,
            eventInfo,
            lastRun,
            lastRunResult,
            lastRunMessage,
            nextPlannedRun,
        } = this.state.service;

        const isDirty = !_.isEqual(this.state.service, this.state.originalService);

		return (
            <section className={`service ${isDirty ? "dirty" : ""}`}>
                <button className="close" onClick={this.close}><span></span></button>
                <h2
                    className="name copy-to-clipboard"
                    title="Click to copy"
                    onClick={() => copyTextToClipboard(name)}
                >
                    {name}
                </h2>
                <p
                    className="windows-service-name dim copy-to-clipboard"
                    title="Click to copy"
                    onClick={() => copyTextToClipboard(windowsServiceDisplayName)}
                >
                    {windowsServiceDisplayName}
                </p>
                <textarea
                    value={description}
                    placeholder="This service has no description."
                    onChange={e => this.onChange("description", e.target.value)}
                    onInput={onTextAreaInput}
                    ref={textarea => textarea && onTextAreaInput({ target: textarea })}
                />
                <div className="settings">
                    <label>
                        <input
                            type="checkbox"
                            checked={alertOnStop}
                            onChange={e => this.onChange("alertOnStop", e.target.checked)}
                        />
                        <span>Alert on stop</span>
                    </label>

                    {/* <label>
                        <input
                            type="number"
                            value={maxRunTime}
                            onChange={e => this.onChange("maxRunTime", parseInt(e.target.value, 10))}
                            min={1}
                            max={1440}
                        />
                        <span>Max run time (minutes)</span>
                    </label> */}

                    <label>
                        <select
                            value={priority}
                            onChange={e => this.onChange("priority", parseInt(e.target.value, 10))}
                        >
                            <option value={0}>Priority: None</option>
                            <option value={1}>Priority: 🔥</option>
                            <option value={5}>Priority: 🔥🔥</option>
                            <option value={10}>Priority: 🔥🔥🔥</option>
                            <option value={15}>Priority: 🔥🔥🔥🔥</option>
                            <option value={20}>Priority: 🔥🔥🔥🔥🔥</option>
                        </select>
                    </label>
                </div>
                <div className="save-cancel" disabled={!isDirty}>
                    <button
                        className="save"
                        onClick={this.save}
                    >
                        Save changes
                    </button>
                    <button
                        className="cancel"
                        onClick={this.cancel}
                    >
                        Cancel changes
                    </button>
                </div>

                <table className="event-info">
                    <tbody>
                        <tr>
                            <td>Next planned run</td>
                            <td></td>
                            <td>
                                <span
                                    className="copy-to-clipboard"
                                    title="Click to copy"
                                    onClick={() => copyTextToClipboard(nextPlannedRun)}
                                >
                                    {Moment(nextPlannedRun).format("YYYY-MM-DD HH:mm:ss")}
                                </span>
                                &nbsp;({Moment(nextPlannedRun).fromNow()})
                            </td>
                        </tr>
                        <tr>
                            <td>Last run</td>
                            <td title={lastRunMessage}>{lastRunResult}</td>
                            <td>
                                <span
                                    className="copy-to-clipboard"
                                    title="Click to copy"
                                    onClick={() => copyTextToClipboard(lastRun)}
                                >
                                    {Moment(lastRun).format("YYYY-MM-DD HH:mm:ss")}
                                </span>
                                &nbsp;({Moment(lastRun).fromNow()})
                            </td>
                        </tr>
                        {!eventInfo && <tr><td>No event info available</td></tr>}
                        {eventInfo && (
                            <React.Fragment>
                                <tr>
                                    <td>First loaded notification</td>
                                    <td>
                                        <span
                                            className="copy-to-clipboard"
                                            title="Click to copy"
                                            onClick={() => copyTextToClipboard(eventInfo.firstLoadedNotificationId)}
                                        >
                                            {eventInfo.firstLoadedNotificationId}
                                        </span>
                                    </td>
                                    <td>
                                        <span
                                            className="copy-to-clipboard"
                                            title="Click to copy"
                                            onClick={() => copyTextToClipboard(eventInfo.firstLoadedNotificationOccuredOn)}
                                        >
                                            {Moment(eventInfo.firstLoadedNotificationOccuredOn).format("YYYY-MM-DD HH:mm:ss")}
                                        </span>
                                        &nbsp;({Moment(eventInfo.firstLoadedNotificationOccuredOn).fromNow()})
                                    </td>
                                </tr>
                                <tr>
                                    <td>Notifications loaded</td>
                                    <td>
                                        <span>{eventInfo.notificationsLoaded}</span>
                                    </td>
                                    <td></td>
                                </tr>
                            </React.Fragment>
                        )}
                    </tbody>
                </table>

                <div className="stats">
                    <Heartbeat
                        show="errors"
                        active={this.state.displayType}
                        noItems={this.state.errors && this.state.errors.numberOfItems}
                        onClick={() => this.setState({ displayType: "errors" })}
                    />
                    <Heartbeat
                        show="logs"
                        active={this.state.displayType}
                        noItems={this.state.logs && this.state.logs.numberOfItems}
                        onClick={() => this.setState({ displayType: "logs" })}
                    />
                    <Heartbeat
                        show="notifications"
                        active={this.state.displayType}
                        noItems={this.state.notifications && this.state.notifications.numberOfItems}
                        onClick={() => this.setState({ displayType: "notifications" })}
                    />
                </div>
                {this.state.displayType === "logs" && this.state.logs && (
                    <section className="errors logs">
                        <Alerts data={this.state.logs} type="log" emptyText="No logs found" />
                    </section>
                )}
                {this.state.displayType === "errors" && this.state.errors && (
                    <section className="errors">
                        <Alerts data={this.state.errors} type="error" emptyText="No errors found" />
                    </section>
                )}
                {this.state.displayType === "notifications" && this.state.notifications && (
                    <section className="errors notifications">
                        <Alerts
                            data={this.state.notifications}
                            type="notification"
                            emptyText="No notifications found" 
                            handleNotificationAction={this.handleNotificationAction}
                        />
                    </section>
                )}
            </section>
		);
	}
};

function onTextAreaInput(e) {
    e.target.style.height = "auto";
    e.target.style.height = (e.target.scrollHeight) + "px";
}


// WEBPACK FOOTER //
// ./src/service.jsx